import { useRef } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { deleteRecipe } from './recipes.service'
import { showUndoToast } from '@/shared/utils/toast'
import type { Recipe } from './types'

export function useDeleteRecipe() {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const { t } = useTranslation('recipes')
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const rejectRef = useRef<((reason?: unknown) => void) | null>(null)

  return useMutation({
    mutationFn: (recipeId: string) =>
      new Promise<string>((resolve, reject) => {
        rejectRef.current = reject
        // Deferred delete: actually call API after 3 seconds
        timerRef.current = setTimeout(async () => {
          try {
            await deleteRecipe(recipeId)
            resolve(recipeId)
          } catch (err) {
            reject(err)
          }
        }, 3000)
      }),
    onMutate: async (recipeId: string) => {
      await queryClient.cancelQueries({ queryKey: ['recipes', 'list'] })
      const prev = queryClient.getQueryData<Recipe[]>(['recipes', 'list'])
      queryClient.setQueryData<Recipe[]>(['recipes', 'list'], (old) =>
        old?.filter((r) => r.id !== recipeId) ?? [],
      )
      navigate({ to: '/recipes' })

      showUndoToast(
        t('delete.undoMessage'),
        t('shell.toast.undo', { ns: 'shell' }),
        () => {
          if (timerRef.current) clearTimeout(timerRef.current)
          rejectRef.current?.(new Error('undone'))
          navigate({ to: '/recipes' })
        },
      )

      return { prev }
    },
    onError: (_err, _id, ctx) => {
      queryClient.setQueryData(['recipes', 'list'], ctx?.prev)
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['recipes', 'list'] })
    },
  })
}
